import React, { useState, useEffect } from 'react';

interface Props {
  initiatedDate?: string; // Date string - can be ISO format or "M/D/YY" format
}

export default function ShippingCountdown({ initiatedDate }: Props) {
  const [timeRemaining, setTimeRemaining] = useState<{
    hours: number;
    minutes: number;
    seconds: number;
    isExpired: boolean;
  }>({ hours: 0, minutes: 0, seconds: 0, isExpired: false });

  useEffect(() => {
    const calculateTimeRemaining = () => {
      // Check if initiatedDate is provided
      if (!initiatedDate) {
        setTimeRemaining({ hours: 0, minutes: 0, seconds: 0, isExpired: true });
        return;
      }

      // Parse the initiated date - handle both ISO format and M/D/YY format
      let initiatedTime: number;
      
      // Check if it's an ISO date string (from API)
      if (initiatedDate.includes('T') || initiatedDate.includes('-')) {
        initiatedTime = new Date(initiatedDate).getTime();
      } else {
        // Handle M/D/YY format (legacy/mock data)
        const dateParts = initiatedDate.split('/');
        if (dateParts.length < 3) {
          setTimeRemaining({ hours: 0, minutes: 0, seconds: 0, isExpired: true });
          return;
        }
        
        const [month, day, year] = dateParts;
        const fullYear = year && year.length === 2 ? `20${year}` : year;
        initiatedTime = new Date(`${month}/${day}/${fullYear}`).getTime();
      }
      
      // Validate the parsed date
      if (isNaN(initiatedTime)) {
        console.error('Invalid initiated date:', initiatedDate);
        setTimeRemaining({ hours: 72, minutes: 0, seconds: 0, isExpired: false });
        return;
      }
      
      // Add 72 hours (72 * 60 * 60 * 1000 milliseconds)
      const deadline = initiatedTime + (72 * 60 * 60 * 1000);
      const now = new Date().getTime();
      const difference = deadline - now;

      if (difference <= 0) {
        setTimeRemaining({ hours: 0, minutes: 0, seconds: 0, isExpired: true });
        return;
      }

      const hours = Math.floor(difference / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeRemaining({ hours, minutes, seconds, isExpired: false });
    };

    // Calculate immediately
    calculateTimeRemaining();

    // Update every second
    const interval = setInterval(calculateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, [initiatedDate]);

  const formatTime = () => {
    if (timeRemaining.isExpired) {
      return 'Overdue';
    }

    const { hours, minutes, seconds } = timeRemaining;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const getProgressPercentage = () => {
    if (timeRemaining.isExpired) return 0;
    
    const totalMs = 72 * 60 * 60 * 1000; // 72 hours in milliseconds
    const remainingMs = (timeRemaining.hours * 60 * 60 * 1000) + 
                       (timeRemaining.minutes * 60 * 1000) + 
                       (timeRemaining.seconds * 1000);
    
    return Math.max(0, Math.min(100, (remainingMs / totalMs) * 100));
  };

  const getProgressBarClass = () => {
    const percentage = getProgressPercentage();
    
    if (timeRemaining.isExpired) return 'progress-bar overdue';
    if (percentage < 25) return 'progress-bar critical'; // Less than 18 hours remaining
    if (percentage < 50) return 'progress-bar warning';  // Less than 36 hours remaining
    return 'progress-bar';
  };

  return (
    <div className="estimated-delivery">
      <span className="delivery-label">
        {timeRemaining.isExpired ? 'Shipping deadline' : 'Time remaining to ship'}
      </span>
      <div className={`delivery-time ${timeRemaining.isExpired ? 'overdue' : ''}`}>
        {formatTime()}
      </div>
      <div className="delivery-progress">
        <div 
          className={getProgressBarClass()}
          style={{ width: `${getProgressPercentage()}%` }}
        ></div>
      </div>
    </div>
  );
}